import {
  type EncounterRejected,
  type EncounterView,
  getItem,
  type InventoryState,
  type PeddlerBought,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { benchHead, itemRow, markIcon, purseChip, setPurse } from './bench.js';
import { encounterRefusalText, lotsText } from './encounter-text.js';
import { closeButton } from './hud.js';
import { describeOnHover, hideItemHover } from './item-hover.js';
import { kitTitle } from './kit.js';
import { formatGold } from './merchant-row.js';
import { uiFormat, uiText } from './text.js';
import './smith.css';
import './merchant.css';
import './peddler.css';

/**
 * The road peddler's window (ADR 0093): his one lot, its count and its price
 * for the whole of it, set against the purse, and one button. It only asks
 * (`peddler_buy`); the server checks the reach, the purse and the bag, and
 * whether this player already bought. Closed by the cross, by Escape, or by
 * walking away, like the merchant's.
 */
export class PeddlerPanel {
  private readonly root: HTMLElement;
  private readonly purse: HTMLElement;
  private readonly body: HTMLElement;
  private readonly message: HTMLElement;
  private encounter: EncounterView | undefined;
  private inventory: InventoryState | undefined;
  /** Between a click and the server's answer: one click, one purchase asked. */
  private pending = false;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.root.hidden) this.close();
  };

  constructor(
    private readonly icons: () => CssAtlas | null,
    private readonly onBuy: (encounterId: string) => void,
  ) {
    this.root = document.createElement('section');
    this.root.className = 'smith merchant peddler';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Colporteur'));

    const title = document.createElement('h2');
    title.className = 'smith__title';
    kitTitle(title, 'merchant');
    title.textContent = uiText('Colporteur');
    title.append(
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );

    this.purse = purseChip();
    const head = benchHead(uiText('Un seul lot, vendu entier.'), this.purse);
    this.body = document.createElement('div');
    this.body.className = 'bench__list';
    this.message = document.createElement('p');
    this.message.className = 'smith__message merchant__message';
    this.message.setAttribute('aria-live', 'polite');

    this.root.append(title, head, this.body, this.message);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKey);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** The encounter whose lot is shown, while the window is open. */
  get encounterId(): string | undefined {
    return this.isOpen ? this.encounter?.id : undefined;
  }

  open(encounter: EncounterView): void {
    this.encounter = encounter;
    this.pending = false;
    this.root.hidden = false;
    this.say('', 'success');
    this.render();
  }

  close(): void {
    if (this.root.hidden) return;
    this.root.hidden = true;
    this.pending = false;
    hideItemHover();
  }

  /** The encounter again, as the last `encounters` said; gone, the window closes. */
  setEncounter(encounter: EncounterView | undefined): void {
    if (this.root.hidden) return;
    if (encounter === undefined) {
      this.close();
      return;
    }
    this.encounter = encounter;
    this.render();
  }

  setInventory(state: InventoryState): void {
    this.inventory = state;
    this.render();
  }

  showBought(bought: PeddlerBought): void {
    if (bought.encounterId !== this.encounter?.id) return;
    this.pending = false;
    this.say(
      uiFormat('Acheté : {what} pour {gold}.', {
        what: lotsText([bought]),
        gold: formatGold(bought.gold),
      }),
      'success',
    );
    this.render();
  }

  showRejected(rejected: EncounterRejected): void {
    this.pending = false;
    this.say(encounterRefusalText(rejected), 'refused');
    this.render();
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }

  private say(text: string, tone: 'success' | 'refused'): void {
    this.message.textContent = text;
    this.message.dataset.tone = tone;
  }

  private render(): void {
    const encounter = this.encounter;
    if (this.root.hidden || encounter === undefined) return;
    const gold = this.inventory?.gold ?? 0;
    setPurse(this.purse, gold);
    hideItemHover();
    this.body.replaceChildren();
    const ware = encounter.ware;
    if (ware === undefined) return;
    const template = getItem(ware.templateId);
    const { row, slot, icon, text, meta, actions } = itemRow(
      template,
      this.icons(),
      undefined,
      ware.templateId,
    );
    markIcon(icon, ware.quantity, 0);
    meta.textContent =
      ware.quantity > 1
        ? uiFormat('Lot de {count}', { count: ware.quantity })
        : uiText('Pièce unique');
    const price = document.createElement('span');
    const short = gold < ware.price;
    price.className =
      short && !encounter.done ? 'bench__price bench__price--short' : 'bench__price';
    price.textContent = formatGold(ware.price);
    text.append(price);
    describeOnHover(slot, template);
    describeOnHover(text, template);

    const buy = document.createElement('button');
    buy.type = 'button';
    buy.className = 'smith__go merchant__sell';
    buy.textContent = uiText(encounter.done ? 'Déjà acheté' : 'Acheter');
    buy.disabled = encounter.done || short || this.pending;
    buy.addEventListener('click', () => {
      if (this.pending || encounter.done) return;
      this.pending = true;
      buy.disabled = true;
      this.say('', 'success');
      this.onBuy(encounter.id);
    });
    actions.append(buy);
    this.body.append(row);
  }
}
